import React, { Key, useEffect, useMemo, useState } from 'react';

import styleText from '../../../shared/components/rbh-text/styles';
import { RBHBox, RBHButton, RBHSwitch, RBHText, RBHTextInput, TagCount } from '../../../shared/components';
import { Form, Row } from 'antd';
import { EditOutlined, ExclamationCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { Colors } from '../../../shared/theme';
import { textStyles } from '../../../shared/theme/fonts-ppv';
import RBHTable2 from '../../../shared/components/rbh-table-2';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { modifierItemCreators } from './redux/modifier-item-action';
import { MODIFIER_ITEM_KEY, modifierItemStateSelector } from './redux/modifier-item-reducer';
import { ModifierItem } from './redux/modifier-item-type';
import { generalCreators } from '../../../redux/general/general-action';
import { GENERAL_STATE_KEY, generalStateSelector } from '../../../redux/general/general-reducer';
import { MODAL_TYPE } from '../../../redux/general/general-constant';
import { httpCreators } from '@ppvrbh/merchant-frontend-core';
import { MODIFIER_ITEM_REQUEST_ID_LIST } from './redux/modifier-item-constant';
import { modifierItemSagas } from './redux/modifier-item-saga';
import { ModifierItemService } from './redux/modifier-item-service';
import ModifierItemFormModal from './components/ModifierItemFormModal';
import { DEBOUNCE_TIMEOUT } from '../../../shared/helpers/constant';

import AddButton from '../components/Button/AddButton';
import { reportCreators } from '../Report/redux/report-action';
import metrics from '../../../shared/theme/metrics';

const ModifierItemScreen = () => {
    const dispatch = useDispatch();
    const modifierItems = useSelector(modifierItemStateSelector(MODIFIER_ITEM_KEY.MODIFIER_ITEMS));
    const meta = useSelector(modifierItemStateSelector(MODIFIER_ITEM_KEY.META));
    const totalItems = useSelector(modifierItemStateSelector(MODIFIER_ITEM_KEY.TOTAL_ITEMS));

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [searchKeyword, setSearchKeyword] = useState('');
    const [debouncedKeyword, setDebouncedKeyword] = useState('');
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingItem, setEditingItem] = useState<ModifierItem | null>(null);
    const [form] = Form.useForm();

    const appConfig = useSelector(generalStateSelector(GENERAL_STATE_KEY.REMOTE_CONFIG));
    const maxItem = Number(appConfig?.find(item => item.key === 'modifier.item.maximum')?.value) || 0;
      
    const tableData = (modifierItems ?? []).map((item: ModifierItem) => ({
        ...item,
        id: item.menuModifierItemId,
    }));

    const loadData = (page = currentPage, limit = pageSize, keyword = debouncedKeyword) => {
        dispatch(modifierItemCreators.callModifierItems({ page, limit, keyword }));
    }

    useEffect(() => {
        loadData(currentPage, pageSize, debouncedKeyword);
    }, [currentPage, pageSize, debouncedKeyword]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedKeyword(searchKeyword);
            setCurrentPage(1);
        }, DEBOUNCE_TIMEOUT);
        return () => clearTimeout(timer);
    }, [searchKeyword]);

    useEffect(() => {
        if (!appConfig) {
            dispatch(reportCreators.callConfigAndShopList(() => {}, () => {}));
        }
    }, []);

    const rowSelection = {
        selectedRowKeys: selectedIds,
        onChange: (keys: Key[]) => setSelectedIds(keys.map(Number)),
    };

    const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchKeyword(value);
    }

    const handleDelete = () => {
        dispatch(httpCreators.request({
            id: MODIFIER_ITEM_REQUEST_ID_LIST.DELETE_MODIFIER_ITEMS,
            sagaHandler: modifierItemSagas.callbackWithErrorHandler,
            service: ModifierItemService.deleteModifierItems,
            serviceParams: selectedIds,
            callbackParams: {
                onSuccess: () => {
                    setSelectedIds([]);
                    loadData();
                },
            },
        }));
    }

    const handleToggleStatus = (item: ModifierItem, checked: boolean) => {
        dispatch(httpCreators.request({
            id: MODIFIER_ITEM_REQUEST_ID_LIST.TOGGLE_MODIFIER_ITEM_STATUS,
            sagaHandler: modifierItemSagas.toggleStatusHandler,
            service: ModifierItemService.toggleModifierItemStatus,
            serviceParams: { menuModifierItemId: item.menuModifierItemId, isOffline: !checked },
            callbackParams: loadData,
        }));
    }

    const onDeleteItems = () => {
        dispatch(generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
            visible: true,
            onOk: handleDelete,
            modalTitle: 'main.menu.modifieritem.delete.confirm.title',
            modalContent: 'main.menu.modifieritem.delete.confirm.content',
            type: MODAL_TYPE.DELETE,
            confirmButtonStyle: { background: Colors.red600, borderColor: Colors.red600 },
        }));
    }

    const onOpenCreateModal = () => {
        form.resetFields();
        setEditingItem(null);
        setIsFormModalOpen(true);
    }

    const onOpenEditModal = (record: ModifierItem) => {
        setEditingItem(record);
        form.setFieldsValue({
            menuModifierItemName: record.menuModifierItemName,
            menuModifierItemNameEN: record.menuModifierItemNameEN,
            menuModifierItemPrice: String(record.menuModifierItemPrice),
            menuModifierItemSKUId: record.menuModifierItemSKUId,
        });
        setIsFormModalOpen(true);
    }

    const onCloseModal = () => {
        setIsFormModalOpen(false);
    }

    const DUPLICATE_KEY_TO_FIELD: Record<string, { field: string; }> = {
        menuModifierItemName_is_duplicated: { field: 'menuModifierItemName' },
        menuModifierItemNameEN_is_duplicated: { field: 'menuModifierItemNameEN' },
    };

    const onError = (translationKeys: string[]) => {
        const fields = translationKeys
            .filter((key) => DUPLICATE_KEY_TO_FIELD[key])
            .map((key) => ({
                name: DUPLICATE_KEY_TO_FIELD[key].field,
                errors: ['ชื่อตัวเลือกซ้ำ กรุณาตั้งชื่อใหม่'],
            }));
        if (fields.length > 0) {
            form.setFields(fields);
        }
    };

    const handleCreate = async () => {
        const values = await form.validateFields();
        setIsSubmitting(true);
        dispatch(httpCreators.request({
            id: MODIFIER_ITEM_REQUEST_ID_LIST.CREATE_MODIFIER_ITEM,
            sagaHandler: modifierItemSagas.callbackWithErrorHandler,
            service: ModifierItemService.createModifierItem,
            serviceParams: {
                menuModifierItemName: values.menuModifierItemName,
                menuModifierItemNameEN: values.menuModifierItemNameEN || undefined,
                menuModifierItemPrice: Number(values.menuModifierItemPrice),
                menuModifierItemSKUId: values.menuModifierItemSKUId || undefined,
            },
            callbackParams: {
                onSuccess: () => {
                    setIsSubmitting(false);
                    setIsFormModalOpen(false);
                    loadData();
                },
                onError: (keys: string[]) => {
                    setIsSubmitting(false);
                    onError(keys);
                },
            },
        }));
    }

    const handleUpdate = async () => {
        if (!editingItem) return;
        const values = await form.validateFields();
        setIsSubmitting(true);
        dispatch(httpCreators.request({
            id: MODIFIER_ITEM_REQUEST_ID_LIST.UPDATE_MODIFIER_ITEM,
            sagaHandler: modifierItemSagas.callbackWithErrorHandler,
            service: ModifierItemService.updateModifierItem,
            serviceParams: {
                menuModifierItemId: editingItem.menuModifierItemId,
                menuModifierItemName: values.menuModifierItemName,
                menuModifierItemNameEN: values.menuModifierItemNameEN || '',
                menuModifierItemPrice: Number(values.menuModifierItemPrice),
                menuModifierItemSKUId: values.menuModifierItemSKUId || '',
            },
            callbackParams: {
                onSuccess: () => {
                    setIsSubmitting(false);
                    setIsFormModalOpen(false);
                    setEditingItem(null);
                    loadData();
                },
                onError: (keys: string[]) => {
                    setIsSubmitting(false);
                    onError(keys);
                },
            },
        }));
    }

    const isMaxItemReached = useMemo(() => {
        if(totalItems === undefined) return false;
        return totalItems >= maxItem;
    },[totalItems, maxItem])

    return (
        <RBHBox>
            {/* Header */}
            <RBHBox style={{ borderBottom: '1px solid #FBFBFD', paddingBottom: '16px', marginBottom: '16px' }}>
                <RBHText style={styleText.headerText} text={'main.menu.modifieritem.title'} />
                <RBHText style={styleText.subTitleText} text={'main.menu.modifieritem.subtitle'} />
            </RBHBox>
            {/* Content */}
            <RBHBox style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Row justify='space-between'>
                    <Row align='middle'  style={{ gap: 8 }}>
                        <RBHText style={{ ...textStyles.h4, color: Colors.grey800 }}  text={'main.menu.modifieritem.total'} />
                        <TagCount id="modifier-item-total-count" count={totalItems ?? 0} maximum={maxItem} />
                           
                         <RBHTextInput
                            prefix={<SearchOutlined style={{ color: Colors.grey600 }} translate={null} />}
                            placeholder='main.menu.modifieritem.search.placeholder'
                            id={'search-modifierItem-input'}
                            style={{ width: '240px', borderColor: Colors.grey100, borderRadius: '8px' }}
                            value={searchKeyword}
                            onChange={onSearchChange}
                        />
                    </Row>

                    <Row align='middle' style={{ gap: 8 }}>
                        {selectedIds.length > 0 && (
                            <Row align='middle' style={{ gap: 8 }}>
                                <RBHText style={{ ...textStyles.subtitle2, color: Colors.grey600 }} text={'main.menu.modifieritem.selected.count'} values={{ count: selectedIds.length }} />
                                <RBHButton
                                    id={'delete-modifierItem-button'}
                                    style={{
                                        ...textStyles.subtitle2,
                                        color: Colors.white,
                                        borderRadius: '48px',
                                        borderColor: Colors.red600,
                                        background: Colors.red600,
                                    }}
                                    text={'main.menu.modifieritem.delete.button'}
                                    type='primary'
                                    onClick={onDeleteItems}
                                />
                            </Row>
                        )}
                        {isMaxItemReached &&
                            <Row align='middle' style={{ gap: 4 }}>
                                <ExclamationCircleOutlined translate={null} style={{ color: Colors.red600, fontSize: 16 }} />
                                <RBHText style={{ ...textStyles.subtitle2, color: Colors.red600 }} text={'main.menu.modifieritem.max.reached'} />
                            </Row>
                        }
                        <AddButton 
                            id={'add-modifierItem-button'} 
                            text={'main.menu.modifieritem.add.button'} 
                            onClick={onOpenCreateModal} 
                            disabled={isMaxItemReached} />
                    </Row>
                </Row>
               <RBHTable2
                    dataSource={tableData}
                    rowSelection={rowSelection}
                    scroll={{ y: 'calc(100vh - 300px)', x: 'auto' }}
                    pagination={{
                        showSizeChanger: true,
                        current: currentPage,
                        pageSize: pageSize,
                        total: meta?.totalItems ?? 0,
                    }}
				    onChange={(value) => {
                        if(value.current) setCurrentPage(value.current);
                        if (value.pageSize) setPageSize(value.pageSize);
                    }}
                    columns={[
                        { title: <FormattedMessage id="main.menu.modifieritem.column.name" />, dataIndex: 'menuModifierItemName', width: metrics.tableHeader.width180 },
                        { title: <FormattedMessage id="main.menu.modifieritem.column.nameEN" />, dataIndex: 'menuModifierItemNameEN', width: metrics.tableHeader.width200 },
                        { title: <FormattedMessage id="main.menu.modifieritem.column.price" />, dataIndex: 'menuModifierItemPrice', width: metrics.tableHeader.width150 },
                        { title: <FormattedMessage id="main.menu.modifieritem.column.sku_id" />, dataIndex: 'menuModifierItemSKUId', width: metrics.tableHeader.width120 },
                        {
                            title: <FormattedMessage id="main.menu.modifieritem.column.status" />,
                            dataIndex: 'menuModifierItemIsOffline',
                            align: 'center',
                            render: (isOffline: boolean, record: ModifierItem) => (
                                <Row justify='center' align='middle' style={{ gap: 8 }}>
                                    <RBHSwitch
                                        id={`status-${record.menuModifierItemId}`}
                                        checked={!isOffline}
                                        size='small'
                                        onChange={(checked: boolean) => handleToggleStatus(record, checked)}
                                    />
                                    <RBHText
                                        style={{ ...textStyles.subtitle3, color: isOffline ? Colors.red600 : Colors.green600 }}
                                        text={isOffline ? 'main.menu.modifieritem.column.status.offline' : 'main.menu.modifieritem.column.status.online'} />
                                </Row>

                            ),
                            width: metrics.tableHeader.width120
                        },
                        {
                            title: 'Action',
                            align: 'center',
                            render: (_, record: ModifierItem) => (
                                <EditOutlined
                                    id={`edit-${record.menuModifierItemId}`}
                                    translate={null}
                                    style={{ color: Colors.grey600, fontSize: 18 }}
                                    onClick={() => onOpenEditModal(record)}
                                />
                            ),
                            width: metrics.tableHeader.width120
                        },
                    ]}
                />
            </RBHBox>

            <ModifierItemFormModal
                isOpen={isFormModalOpen}
                form={form}
                onCloseModal={onCloseModal}
                handleSubmit={editingItem ? handleUpdate : handleCreate}
                isEditing={!!editingItem}
                isLoading={isSubmitting}
            />
            
        </RBHBox>
    )
}

export default ModifierItemScreen;
